import React, { useState } from 'react';
import { fmtEur } from '../../utils/formatArcaNumbers';
import { BarraDelAnio as DatosBarra } from '../../utils/resumenMulticuenta/agregado';

/**
 * Tonos de los segmentos, los de Proa (`planShading.ts`): verdes grisáceos sólidos, nunca el
 * verde vivo de `--primary` ni negro con opacidad (se leería como «deshabilitado»).
 */
const TONOS = ['#1F3328', '#3D5C49', '#6C8A78', '#A3BBAD'];
const ATENUADO = 0.35;

export interface BarraDelAnioProps {
	barra: DatosBarra;
	/** El nombre de cada plan (el del plan en proceso puede no tenerlo). */
	nombreDe: (nombre: string | null) => string;
	translate: (clave: string) => string;
	/** `AAAA` de un año pasado (el selector de año de arriba). Sin él, el año en curso. */
	anio?: string;
}

/**
 * La barra del año (`ContributionsBar` / `RedemptionsBar` de Proa): un segmento por plan, contra
 * el tope (aportaciones) o contra lo ahorrado (rescates). Al pasar por un segmento, su plan y su
 * importe; los demás se atenúan.
 */
const BarraDelAnio: React.FC<BarraDelAnioProps> = ({ barra, nombreDe, translate, anio }) => {
	const [encima, setEncima] = useState<string | null>(null);
	const t = (clave: string) => translate(`resumen.bar.${clave}`);
	// El texto de Proa: en aportaciones, también lo que queda hasta el tope (nunca negativo). En un
	// año pasado no queda nada por aportar: sin «quedan».
	const deAnio = anio ? 'Year' : '';
	const etiqueta = (item: DatosBarra['items'][number]) =>
		t(`${barra.modo === 'rescates' ? 'segmentRescues' : 'segmentContributions'}${deAnio}`)
			.replace('{anio}', anio ?? '')
			.replace('{plan}', nombreDe(item.nombre))
			.replace('{importe}', fmtEur(item.importe))
			.replace('{restante}', fmtEur(Math.max(barra.tope - barra.total, 0)))
			.replace('{tope}', fmtEur(barra.tope));
	const elegido = barra.items.find((item) => item.planId === encima);

	return (
		<div>
			<div className="flex items-baseline justify-between gap-3">
				<p className="text-sm font-medium text-foreground">
					{t(`${barra.modo === 'rescates' ? 'rescues' : 'contributions'}${deAnio}`).replace(
						'{anio}',
						anio ?? ''
					)}
				</p>
				<span className="text-sm font-medium whitespace-nowrap">
					{fmtEur(barra.total)} / {fmtEur(barra.tope)}
				</span>
			</div>
			<div className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-muted">
				{barra.items.map((item, indice) => (
					<div
						key={item.planId}
						role="img"
						tabIndex={0}
						aria-label={etiqueta(item)}
						className="h-full outline-none transition-opacity"
						style={{
							width: `${Math.min((item.importe / barra.tope) * 100, 100)}%`,
							backgroundColor: TONOS[indice % TONOS.length],
							opacity: encima !== null && encima !== item.planId ? ATENUADO : 1
						}}
						onMouseEnter={() => setEncima(item.planId)}
						onMouseLeave={() => setEncima(null)}
						onFocus={() => setEncima(item.planId)}
						onBlur={() => setEncima(null)}
					/>
				))}
			</div>
			<p className="mt-1.5 min-h-[1rem] text-xs text-muted-foreground" aria-hidden="true">
				{elegido ? etiqueta(elegido) : ' '}
			</p>
			{!barra.completa && <p className="text-xs text-muted-foreground">{t('incomplete')}</p>}
		</div>
	);
};

export default BarraDelAnio;
